import React, { useEffect, useMemo, useState } from 'react';
import { Plus, X, Save, Trash2, Shield, Sparkles, AlertCircle, Lock, Search, Copy } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

/* -------------------------------------------------------------------------- */
/*  Role Builder — Phase 2 of permissions.py                                  */
/*                                                                            */
/*  Lists built-in + custom roles for the current tenant. Lets admins create  */
/*  / edit / delete custom roles by ticking permissions from the catalog.     */
/*  Built-in roles are read-only (lock icon).                                 */
/* -------------------------------------------------------------------------- */

const GROUP_RULES = [
  { id: 'read',    label: 'Read access',        match: (p) => p.startsWith('view_') },
  { id: 'docs',    label: 'Documents',          match: (p) => p.includes('document') },
  { id: 'mgmt',    label: 'Management',         match: (p) => p.startsWith('manage_') },
  { id: 'ai',     label: 'AI & advisors',     match: (p) => p.includes('ai_') || p.includes('train') || p.includes('council') || p.includes('mentors') || p.includes('drafts') },
  { id: 'audit',   label: 'Audit & security',   match: (p) => p.includes('audit') || p.includes('security') },
  { id: 'tenant', label: 'Tenant & billing',  match: (p) => p.includes('tenant') || p.includes('billing') || p.includes('subscription') || p.includes('bank') },
  { id: 'other',  label: 'Other',              match: () => true },
];

function groupPermissions(allPerms) {
  const out = { read: [], docs: [], mgmt: [], ai: [], audit: [], tenant: [], other: [] };
  const taken = new Set();
  for (const p of allPerms) {
    for (const g of GROUP_RULES) {
      if (taken.has(p)) continue;
      if (g.match(p)) { out[g.id].push(p); taken.add(p); break; }
    }
  }
  return out;
}

function prettify(perm) {
  return perm.replace(/^(view|manage|train|impersonate|convene|upload|delete|submit)_/, (m, p1) => `${p1[0].toUpperCase()}${p1.slice(1)} `).replace(/_/g, ' ');
}

const initialDraft = () => ({
  role_id: '',
  name: '',
  description: '',
  permissions: [],
});

/* eslint-disable -- v6 react-hooks/set-state-in-effect false-positive on the fetch + role-edit effects. */
export default function RolesAdmin() {
  const [data, setData] = useState({ builtin: [], custom: [], all_permissions: [], whitelabel_denied: [] });
  const [selected, setSelected] = useState(null);     // role_id selected for editing
  const [draft, setDraft] = useState(null);            // edit draft for selected role
  const [creating, setCreating] = useState(false);
  const [newDraft, setNewDraft] = useState(initialDraft());
  const [filter, setFilter] = useState('');
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get('/admin/roles');
      setData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load roles.');
    }
  };
  useEffect(() => { load(); }, []);

  const grouped = useMemo(() => groupPermissions(data.all_permissions || []), [data.all_permissions]);
  const denied = useMemo(() => new Set(data.whitelabel_denied || []), [data.whitelabel_denied]);

  const allRoles = useMemo(() => {
    return [
      ...(data.builtin || []).map((r) => ({ ...r, _kind: 'builtin' })),
      ...(data.custom || []).map((r) => ({ ...r, _kind: 'custom' })),
    ];
  }, [data]);

  const selectedRole = useMemo(() => allRoles.find((r) => r.role_id === selected) || null, [allRoles, selected]);

  const startEdit = (role) => {
    setSelected(role.role_id);
    setCreating(false);
    if (role.is_builtin) {
      setDraft(null); // built-in = read-only
      return;
    }
    setDraft({
      role_id: role.role_id,
      name: role.name || '',
      description: role.description || '',
      permissions: [...(role.permissions || [])],
    });
  };

  const startCreate = () => {
    setSelected(null);
    setDraft(null);
    setCreating(true);
    setNewDraft(initialDraft());
  };

  const cloneFrom = (role) => {
    // Pre-fill the create form with a suggested slug + the role's permission set.
    setSelected(null);
    setDraft(null);
    setCreating(true);
    setNewDraft({
      role_id: `${role.role_id}_custom`.slice(0, 32),
      name: `${role.name} (custom)`,
      description: `Cloned from ${role.name}. Tune the permissions to taste.`,
      permissions: [...(role.permissions || [])],
    });
    toast.info(`Cloned ${role.name} → ${role.permissions.length} permissions pre-selected.`);
  };

  const togglePerm = (perm, target) => {
    const setter = target === 'edit' ? setDraft : setNewDraft;
    setter((d) => {
      if (!d) return d;
      const cur = new Set(d.permissions || []);
      if (cur.has(perm)) cur.delete(perm); else cur.add(perm);
      return { ...d, permissions: [...cur] };
    });
  };

  const saveEdit = async () => {
    if (!draft || !selectedRole) return;
    setSaving(true);
    try {
      await api.patch(`/admin/roles/${selectedRole.role_id}`, {
        name: draft.name.trim(),
        description: draft.description.trim(),
        permissions: draft.permissions,
      });
      toast.success(`Updated "${draft.name}".`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save.');
    } finally { setSaving(false); }
  };

  const saveCreate = async () => {
    if (!newDraft.role_id.trim() || !newDraft.name.trim()) {
      toast.error('Role id and name are required.');
      return;
    }
    setSaving(true);
    try {
      const { data: created } = await api.post('/admin/roles', {
        role_id: newDraft.role_id.trim().toLowerCase(),
        name: newDraft.name.trim(),
        description: newDraft.description.trim(),
        permissions: newDraft.permissions,
      });
      toast.success(`Created role "${created.name}".`);
      setCreating(false);
      setNewDraft(initialDraft());
      await load();
      setSelected(created.role_id);
      setDraft({
        role_id: created.role_id,
        name: created.name,
        description: created.description || '',
        permissions: [...(created.permissions || [])],
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not create role.');
    } finally { setSaving(false); }
  };

  const removeRole = async () => {
    if (!selectedRole || selectedRole.is_builtin) return;
    if (!window.confirm(`Delete custom role "${selectedRole.name}"? Users still on this role must be re-assigned first.`)) return;
    setSaving(true);
    try {
      await api.delete(`/admin/roles/${selectedRole.role_id}`);
      toast.success('Role deleted.');
      setSelected(null);
      setDraft(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not delete role.');
    } finally { setSaving(false); }
  };

  const editingDraft = creating ? newDraft : draft;
  const editingSet = new Set(editingDraft?.permissions || []);
  const isEditable = creating || (selectedRole && !selectedRole.is_builtin);

  const filteredPerms = (perms) => {
    const f = filter.trim().toLowerCase();
    if (!f) return perms;
    return perms.filter((p) => p.toLowerCase().includes(f));
  };

  return (
    <div className="max-w-7xl mx-auto px-4 lg:px-6 py-8" data-testid="roles-admin-page">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <div className="ve-eyebrow text-[#5E1F8B] dark:text-[#8B3FC5] flex items-center gap-2">
            <Shield size={14} /> Role builder
          </div>
          <h1 className="display text-3xl md:text-4xl mt-2 font-bold">
            Define your own roles. <span className="ve-gold-text">Compose permissions like Lego.</span>
          </h1>
          <p className="text-sm text-slate-600 dark:text-white/65 mt-2 max-w-2xl">
            Built-in roles ship locked for stability. Add custom roles tailored to your operation — chief-of-staff, deal lead, observer-only, anything you need. Every permission is granular and the audit log records who created what.
          </p>
        </div>
        <button
          onClick={startCreate}
          data-testid="roles-create-btn"
          className="ve-btn-primary text-sm inline-flex items-center gap-1.5"
        >
          <Plus size={14} /> New custom role
        </button>
      </div>

      <div className="grid lg:grid-cols-[280px_1fr] gap-4">
        {/* Role list (LEFT) */}
        <div className="ve-card p-3" data-testid="roles-list">
          <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-400 dark:text-white/45 px-2 py-1.5">Built-in · {data.builtin?.length || 0}</div>
          <ul className="space-y-0.5">
            {(data.builtin || []).map((r) => (
              <li key={r.role_id}>
                <button
                  onClick={() => startEdit({ ...r, is_builtin: true })}
                  data-testid={`role-row-${r.role_id}`}
                  className={`w-full text-left px-2.5 py-1.5 rounded-md text-sm flex items-center justify-between gap-2 transition ${
                    selected === r.role_id && !creating
                      ? 'bg-[#FFF8E5] dark:bg-[#3a2f0a] text-slate-900 dark:text-white'
                      : 'hover:bg-slate-50 dark:hover:bg-white/[0.04] text-slate-700 dark:text-white/75'
                  }`}
                >
                  <span className="flex items-center gap-2 min-w-0">
                    <Lock size={11} className="text-slate-400 dark:text-white/40 shrink-0" />
                    <span className="truncate">{r.name}</span>
                  </span>
                  <span className="text-[10px] text-slate-400 dark:text-white/40 shrink-0">{r.permissions.length}</span>
                </button>
              </li>
            ))}
          </ul>
          <div className="mt-3 text-[10px] uppercase tracking-[0.22em] font-bold text-slate-400 dark:text-white/45 px-2 py-1.5">Custom · {data.custom?.length || 0}</div>
          {(!data.custom || data.custom.length === 0) && (
            <p className="text-xs text-slate-400 dark:text-white/40 px-2 py-2 italic">None yet. Click &ldquo;New custom role&rdquo; above to define one.</p>
          )}
          <ul className="space-y-0.5">
            {(data.custom || []).map((r) => (
              <li key={r.role_id}>
                <button
                  onClick={() => startEdit({ ...r, is_builtin: false })}
                  data-testid={`role-row-${r.role_id}`}
                  className={`w-full text-left px-2.5 py-1.5 rounded-md text-sm flex items-center justify-between gap-2 transition ${
                    selected === r.role_id && !creating
                      ? 'bg-[#FFF8E5] dark:bg-[#3a2f0a] text-slate-900 dark:text-white'
                      : 'hover:bg-slate-50 dark:hover:bg-white/[0.04] text-slate-700 dark:text-white/75'
                  }`}
                >
                  <span className="flex items-center gap-2 min-w-0">
                    <Sparkles size={11} className="text-[#8B3FC5] shrink-0" />
                    <span className="truncate">{r.name}</span>
                  </span>
                  <span className="text-[10px] text-slate-400 dark:text-white/40 shrink-0">{r.permissions.length}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>

        {/* Editor (RIGHT) */}
        <div className="ve-card p-5 lg:p-6 min-h-[440px]">
          {!creating && !selectedRole && (
            <div className="h-full flex items-center justify-center text-center py-12">
              <div>
                <Shield size={32} className="mx-auto text-slate-300 dark:text-white/15" />
                <p className="mt-3 text-sm text-slate-500 dark:text-white/55 max-w-md mx-auto">
                  Pick a role from the list to inspect its permissions — or click <b>+ New custom role</b> to compose your own.
                </p>
              </div>
            </div>
          )}

          {(creating || selectedRole) && (
            <>
              {/* Header / identity */}
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex-1 min-w-0">
                  {creating ? (
                    <>
                      <div className="ve-eyebrow text-[#5E1F8B] dark:text-[#8B3FC5]">New custom role</div>
                      <div className="mt-2 grid sm:grid-cols-2 gap-3">
                        <label className="block">
                          <span className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-400 dark:text-white/45">Slug (lower_snake_case)</span>
                          <input
                            type="text"
                            value={newDraft.role_id}
                            onChange={(e) => setNewDraft((d) => ({ ...d, role_id: e.target.value }))}
                            placeholder="deal_lead"
                            data-testid="roles-new-slug"
                            className="mt-1 w-full rounded-md border border-slate-200 dark:border-white/10 bg-white dark:bg-white/5 px-3 py-2 text-sm font-mono focus:outline-none focus:border-[#8B3FC5]"
                          />
                        </label>
                        <label className="block">
                          <span className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-400 dark:text-white/45">Display name</span>
                          <input
                            type="text"
                            value={newDraft.name}
                            onChange={(e) => setNewDraft((d) => ({ ...d, name: e.target.value }))}
                            placeholder="Deal Lead"
                            data-testid="roles-new-name"
                            className="mt-1 w-full rounded-md border border-slate-200 dark:border-white/10 bg-white dark:bg-white/5 px-3 py-2 text-sm focus:outline-none focus:border-[#8B3FC5]"
                          />
                        </label>
                      </div>
                      <label className="block mt-3">
                        <span className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-400 dark:text-white/45">Description</span>
                        <input
                          type="text"
                          value={newDraft.description}
                          onChange={(e) => setNewDraft((d) => ({ ...d, description: e.target.value }))}
                          placeholder="What does this role do?"
                          data-testid="roles-new-desc"
                          className="mt-1 w-full rounded-md border border-slate-200 dark:border-white/10 bg-white dark:bg-white/5 px-3 py-2 text-sm focus:outline-none focus:border-[#8B3FC5]"
                        />
                      </label>
                    </>
                  ) : selectedRole?.is_builtin ? (
                    <>
                      <div className="ve-eyebrow text-slate-400 dark:text-white/45">Built-in role · read-only</div>
                      <h2 className="display text-2xl font-bold mt-1">{selectedRole.name}</h2>
                      <code className="text-[11px] text-slate-400 dark:text-white/40 font-mono">{selectedRole.role_id}</code>
                    </>
                  ) : (
                    <>
                      <div className="ve-eyebrow text-[#5E1F8B] dark:text-[#8B3FC5]">Custom role</div>
                      <input
                        type="text"
                        value={draft?.name || ''}
                        onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
                        data-testid="roles-edit-name"
                        className="display text-2xl font-bold mt-1 bg-transparent w-full focus:outline-none border-b border-transparent focus:border-[#8B3FC5] transition"
                      />
                      <code className="text-[11px] text-slate-400 dark:text-white/40 font-mono">{selectedRole.role_id}</code>
                      <input
                        type="text"
                        value={draft?.description || ''}
                        onChange={(e) => setDraft((d) => ({ ...d, description: e.target.value }))}
                        placeholder="Description"
                        data-testid="roles-edit-desc"
                        className="mt-2 w-full rounded-md border border-slate-200 dark:border-white/10 bg-white dark:bg-white/5 px-3 py-2 text-sm focus:outline-none focus:border-[#8B3FC5]"
                      />
                    </>
                  )}
                </div>
                <div className="flex flex-col gap-2 shrink-0">
                  {isEditable && (
                    <button
                      onClick={creating ? saveCreate : saveEdit}
                      disabled={saving}
                      data-testid="roles-save-btn"
                      className="ve-btn-primary text-sm inline-flex items-center gap-1.5 disabled:opacity-50"
                    >
                      <Save size={13} /> {saving ? 'Saving…' : 'Save'}
                    </button>
                  )}
                  {!creating && selectedRole?.is_builtin && selectedRole.role_id !== 'system_admin' && (
                    <button
                      onClick={() => cloneFrom(selectedRole)}
                      data-testid="roles-clone-btn"
                      className="text-xs uppercase tracking-[0.18em] font-bold px-3 py-1.5 rounded border border-[#8B3FC5]/60 text-[#5E1F8B] dark:text-[#8B3FC5] hover:bg-[#FFF8E5] dark:hover:bg-[#3a2f0a] inline-flex items-center gap-1.5 transition"
                    >
                      <Copy size={11} /> Clone → custom
                    </button>
                  )}
                  {!creating && selectedRole && !selectedRole.is_builtin && (
                    <button
                      onClick={removeRole}
                      disabled={saving}
                      data-testid="roles-delete-btn"
                      className="text-xs uppercase tracking-[0.18em] font-bold px-3 py-1.5 rounded border border-rose-300 text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-900/20 inline-flex items-center gap-1.5 transition"
                    >
                      <Trash2 size={11} /> Delete
                    </button>
                  )}
                  {creating && (
                    <button
                      onClick={() => { setCreating(false); setNewDraft(initialDraft()); }}
                      className="text-xs uppercase tracking-[0.18em] font-bold px-3 py-1.5 rounded border border-slate-200 dark:border-white/10 text-slate-500 hover:bg-slate-50 dark:hover:bg-white/[0.05] inline-flex items-center gap-1.5 transition"
                    >
                      <X size={11} /> Cancel
                    </button>
                  )}
                </div>
              </div>

              {selectedRole?.is_builtin && selectedRole.role_id === 'system_admin' && (
                <div className="mt-4 inline-flex items-center gap-2 px-3 py-2 rounded-md bg-amber-50 dark:bg-amber-900/15 border border-amber-200 dark:border-amber-400/20 text-xs text-amber-700 dark:text-amber-300">
                  <AlertCircle size={13} /> system_admin has every permission and cannot be edited.
                </div>
              )}

              {/* Permission picker */}
              <div className="mt-6">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="ve-eyebrow text-slate-500 dark:text-white/55">
                    Permissions · {editingDraft?.permissions.length || (selectedRole?.permissions || []).length} / {data.all_permissions.length}
                  </div>
                  <div className="relative">
                    <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input
                      type="text"
                      value={filter}
                      onChange={(e) => setFilter(e.target.value)}
                      placeholder="Filter…"
                      data-testid="roles-perm-filter"
                      className="pl-7 pr-3 py-1.5 text-xs rounded-md border border-slate-200 dark:border-white/10 bg-white dark:bg-white/5 focus:outline-none focus:border-[#8B3FC5]"
                    />
                  </div>
                </div>

                <div className="mt-3 space-y-4">
                  {GROUP_RULES.map((g) => {
                    const perms = filteredPerms(grouped[g.id] || []);
                    if (!perms.length) return null;
                    return (
                      <div key={g.id}>
                        <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#5E1F8B] dark:text-[#8B3FC5] mb-2">{g.label}</div>
                        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-1.5">
                          {perms.map((p) => {
                            const isOn = isEditable
                              ? editingSet.has(p)
                              : (selectedRole?.permissions || []).includes(p);
                            const isDenied = isEditable && denied.has(p);
                            return (
                              <label
                                key={p}
                                data-testid={`perm-row-${p}`}
                                className={`flex items-center gap-2 px-2.5 py-1.5 rounded-md text-xs border transition cursor-pointer ${
                                  isOn
                                    ? 'border-[#8B3FC5] bg-[#FFF8E5] dark:bg-[#3a2f0a]'
                                    : 'border-slate-200 dark:border-white/10 hover:border-[#8B3FC5]/40'
                                } ${(!isEditable || isDenied) ? 'cursor-not-allowed opacity-60' : ''}`}
                                title={isDenied ? 'Denied for custom roles' : ''}
                              >
                                <input
                                  type="checkbox"
                                  checked={isOn}
                                  disabled={!isEditable || isDenied}
                                  onChange={() => togglePerm(p, creating ? 'new' : 'edit')}
                                  className="accent-[#8B3FC5] shrink-0"
                                />
                                <span className="truncate font-mono text-[11px]">{prettify(p)}</span>
                                {isDenied && <Lock size={10} className="text-slate-400 ml-auto shrink-0" />}
                              </label>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
